<script lang="ts">
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import AttachmentTable from "../../../shared/components/AttachmentTable.svelte";
	import Breadcrumbs from "../../../shared/components/Breadcrumbs.svelte";
	import Content from "../../../shared/components/Content.svelte";
	import Layout from "../components/Layout.svelte";
	import DynamicPicture from "../../../shared/components/Picture/DynamicPicture.svelte";
	import type { BlogDetailProps } from "./BlogDetailProps.js";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getContext, setContext } from "svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";

	type $$Props = BlogDetailProps;

	const { news, breadcrumbs } = $$restProps as $$Props;

	const { STORAGE_URL, TENANT_LOCALE } = getGlobalSettings();
	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);
	setContext(thumbnailImageContextKey, news.primaryImage ? `${STORAGE_URL}/${news.primaryImage.path}` : undefined);
</script>

<Layout>
	<article class="container">
		{#if news.primaryImage}
			<section class="flex items-center overflow-hidden rounded-lg">
				<DynamicPicture
					bucket="storage"
					image={news.primaryImage}
					alt={news.title}
					width={1424}
					height={0}
					loading="eager"
					imgClass="w-full h-full lg:h-auto object-cover"
					class="flex h-[20rem] w-full items-center justify-center md:h-[36.25rem] lg:h-[28.25rem]"
					sourcesets={{
						"<md": [608, 0],
						">md": [704, 580],
						">lg": [960, 0],
						">xl": [1424, 0],
					}}
				/>
			</section>
		{/if}
		<Breadcrumbs pageSlug={pageSlug[TENANT_LOCALE] ?? ""} {breadcrumbs} />
		<section class="container flex flex-col md:items-center">
			<div class="lg:w-[50rem]">
				<h1 class="m-0 mb-4 p-0 font-normal lg:mb-7 lg:leading-[3.25rem]">
					{nbspify(news.title)}
				</h1>
				<h2 class="text-base font-normal lg:text-[1.5rem] lg:leading-8">{nbspify(news.perex)}</h2>
			</div>
			{#if news.content.length > 0}
				<div class="lg:w-[50rem]">
					<Content content={news.content} />
				</div>
			{/if}
		</section>
		{#if news.images.length > 0}
			<section class="flex flex-col gap-2 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
				{#each news.images as image, index}
					<DynamicPicture
						bucket="storage"
						lightboxId={news.id}
						{image}
						alt="{news.title} – {index + 1}/{news.images.length}"
						width={470}
						lightboxWidth={900}
						height={0}
						loading="eager"
						imgClass="w-full h-full object-cover rounded-lg"
						class="h-[20rem] w-full rounded-lg md:h-[36.25rem] lg:h-[26.25rem]"
					/>
				{/each}
			</section>
		{/if}
		{#if news.attachments.length > 0 && news.attachments}
			<AttachmentTable attachments={news.attachments} class="mt-8" />
		{/if}
	</article>
</Layout>
